import { animated, useSpring } from '@react-spring/web';
import { css, StyleDeclarationValue } from 'aphrodite';
import * as React from 'react';
import { useEffect, useRef } from 'react';
import { v4 as uuid } from 'uuid';
import { Input } from '../../../../../../web/components-v2/Input';
import { Select, SelectContent, SelectItem, SelectTrigger } from '../../../../../../web/components-v2/Select';
import { ISelectOption, MultiSelect } from '../../../../../../web/components/DeprecatedSelect';
import { Toggle } from '../../../../../../web/components/Toggle';
import { AccountDetailsInfoField } from '../../../../../components/accountDetails/AccountDetailsInfoField';
import { styleSheet } from '../styles';

interface ISetting<T> {
	category: SettingCategory;
	info?: string;
	/** Rendered after the input for SettingType.Input rows (e.g. a saving indicator). */
	inputAccessory?: React.ReactNode;
	inputProps?: Partial<React.ComponentProps<typeof Input>>;
	label?: string | React.ReactNode;
	onSelectionChange?: (selectedOption: ISelectOption<T>) => void;
	onMultiSelectionChange?: (selectedOptions: ISelectOption<T>[]) => void;
	onToggle?: (checked: boolean) => void;
	options?: ISelectOption<T>[];
	selectedOption?: ISelectOption<T>;
	selectedOptions?: ISelectOption<T>[];
	settingDisabled?: boolean;
	type: SettingType;
	value?: boolean | (() => boolean);
}

interface IProps<T> {
	canModifyAccount?: boolean;
	disabled?: boolean;
	data: ISetting<T>;
	customText?: string;
	/** The expand/collapse spring animates to a fixed height; override for rows taller than the 56px default. */
	expandedHeight?: number;
	styles?: StyleDeclarationValue[];
	onCustomCallback?(): void;
	/**
	 * `'default'` keeps the fixed-height row that collapses to nothing when disabled.
	 *
	 * `'detail'` is the group view's treatment: the row sizes to its content, shows its help text
	 * beneath the label instead of behind a hover icon, and stays put when disabled so a dependent
	 * setting remains readable — greyed rather than vanishing — while its parent is off.
	 */
	variant?: 'default' | 'detail';
	/** Rendered beside the label. The group view uses it for a parent's dependent count. */
	badge?: React.ReactNode;
}

export enum SettingCategory {
	Administrative = 'Administrative',
	AI = 'AI',
	Dashboard = 'Dashboard',
	EmailTracking = 'Email Tracking',
	Features = 'Features',
	PreApprovedContent = 'Pre Approved Content',
	Permanent = 'Permanent',
	Databoards = 'Databoards',
}

export enum SettingType {
	Dropdown = 'Dropdown',
	Custom = 'Custom',
	// eslint-disable-next-line @typescript-eslint/no-shadow
	Input = 'Input',
	Multi = 'Multi',
	MultiWInput = 'MultiWInput',
	// eslint-disable-next-line @typescript-eslint/no-shadow
	MultiSelect = 'MultiSelect',
	// eslint-disable-next-line @typescript-eslint/no-shadow
	Toggle = 'Toggle',
}

export function Setting<T>({
	badge,
	canModifyAccount,
	data,
	disabled,
	customText,
	expandedHeight = 56,
	onCustomCallback,
	styles,
	variant = 'default',
}: IProps<T>) {
	const id = useRef(uuid()).current;
	const hasRendered = useRef(true);
	const spring = useSpring({
		delay: 100,
		from: { height: !disabled ? 0 : expandedHeight, opacity: !disabled ? 0 : 1 },
		immediate: hasRendered.current,
		to: { height: !disabled ? expandedHeight : 0, opacity: !disabled ? 1 : 0 },
	});

	useEffect(() => {
		hasRendered.current = false;
	}, []);

	const isDetail = variant === 'detail';

	// The category-based left/right borders here used to divide the two side-by-side columns the
	// tab rendered. Rows now sit in a single bordered panel, so those dividers are gone.
	const computedStyles = [
		!canModifyAccount || disabled ? styleSheet.disable : null,
		styleSheet.infoFieldContainerBottom,
		// Comes after the fixed 55px row height above so stacked help text can wrap instead of clip.
		isDetail ? styleSheet.detailRowHeight : null,
		...(styles || []),
	];

	const hasBooleanValue = data.value !== null && data.value !== undefined;
	const booleanValue = hasBooleanValue ? (typeof data.value === 'function' ? data.value() : data.value) : false;

	// Only the badge shares the label's line. Help text stays behind the hover icon in both
	// variants: stacked beneath every label it cost a second line on rows that far outnumber the
	// ones anybody reads the help for.
	const label =
		isDetail && badge ? (
			<span className={css(styleSheet.detailLabelRow)}>
				{data.label}
				{badge}
			</span>
		) : (
			data.label
		);

	const field = (
		<AccountDetailsInfoField
			disabled={disabled || data.settingDisabled}
			label={label}
			info={data.info}
			key={id}
			labelStyles={
				isDetail
					? [styleSheet.infoFieldValueBottom, styleSheet.detailLabelContainer]
					: [styleSheet.infoFieldValueBottom]
			}
			styles={computedStyles}
			valueStyles={
				isDetail
					? [styleSheet.infoFieldValueBottom, styleSheet.detailValueContainer]
					: [styleSheet.infoFieldValueBottom]
			}
		>
			{data.type === SettingType.Dropdown && (
				<Select
					value={data.selectedOption?.id ?? ''}
					onValueChange={value => {
						const option = data.options?.find(o => o.id === value);
						if (option) data.onSelectionChange?.(option);
					}}
				>
					<SelectTrigger disabled={disabled || !canModifyAccount || data.settingDisabled} styles={styleSheet.dropdown}>
						<span>{data.selectedOption?.text ?? ''}</span>
					</SelectTrigger>
					<SelectContent>
						{(data.options || []).map(option => (
							<SelectItem key={option.id} value={option.id}>
								{option.text}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
			)}
			{data.type === SettingType.MultiSelect && (
				<MultiSelect
					disabled={disabled || !canModifyAccount || data.settingDisabled}
					onOptionClick={(option, wasSelected) => {
						if (data.onMultiSelectionChange) {
							const currentSelectedOptions = data.selectedOptions || [];
							const newSelectedOptions = wasSelected
								? [...currentSelectedOptions, option]
								: currentSelectedOptions.filter(o => o.id !== option.id);
							data.onMultiSelectionChange(newSelectedOptions);
						}
					}}
					options={data.options || []}
					selectedOptions={data.selectedOptions || []}
					selectedOptionsTitle={
						data.selectedOptions && data.selectedOptions.length > 0
							? `${data.selectedOptions.length} selected`
							: 'Select options...'
					}
					styles={[styleSheet.dropdown]}
				/>
			)}
			{data.type === SettingType.Toggle && (
				<Toggle
					disabled={disabled || !canModifyAccount || data.settingDisabled}
					id={id}
					isOn={!!booleanValue}
					onToggleCheckChanged={data?.onToggle}
					uncheckedColor='#ccc'
				/>
			)}
			{data.type === SettingType.Input ? (
				<>
					<Input
						{...(data?.inputProps || {})}
						disabled={data?.inputProps?.disabled || disabled || !canModifyAccount || data.settingDisabled}
						id={id}
						type={data?.type || 'text'}
					/>
					{data.inputAccessory}
				</>
			) : null}
			{data.type === SettingType.Custom && (
				<button
					className={!disabled ? css(styleSheet.customRow) : ''}
					onClick={disabled || !canModifyAccount ? null : onCustomCallback}
				>
					{customText}
				</button>
			)}
			{data.type === SettingType.Multi && (
				<>
					{booleanValue && (
						<Select
							value={data.selectedOption?.id ?? ''}
							onValueChange={value => {
								const option = data.options?.find(o => o.id === value);
								if (option) data.onSelectionChange?.(option);
							}}
						>
							<SelectTrigger
								disabled={disabled || !canModifyAccount || data.settingDisabled}
								styles={styleSheet.multiDropdown}
							>
								<span>{data.selectedOption?.text ?? ''}</span>
							</SelectTrigger>
							<SelectContent>
								{(data.options || []).map(option => (
									<SelectItem key={option.id} value={option.id}>
										{option.text}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					)}

					<Toggle
						disabled={disabled || !canModifyAccount || data.settingDisabled}
						id={id}
						isOn={!!booleanValue}
						onToggleCheckChanged={data?.onToggle}
						uncheckedColor='#ccc'
					/>
				</>
			)}
			{data.type === SettingType.MultiWInput && (
				<>
					{booleanValue && (
						<>
							<Input
								{...(data?.inputProps || {})}
								disabled={data?.inputProps?.disabled || disabled || !canModifyAccount || data.settingDisabled}
								id={id}
								type={data?.type || 'text'}
								style={{ width: 100 }}
							/>
							&nbsp;&nbsp;&nbsp;
							<Select
								value={data.selectedOption?.id ?? ''}
								onValueChange={value => {
									const option = data.options?.find(o => o.id === value);
									if (option) data.onSelectionChange?.(option);
								}}
							>
								<SelectTrigger
									disabled={disabled || !canModifyAccount || data.settingDisabled}
									styles={styleSheet.multiDropdown}
								>
									<span>{data.selectedOption?.text ?? ''}</span>
								</SelectTrigger>
								<SelectContent>
									{(data.options || []).map(option => (
										<SelectItem key={option.id} value={option.id}>
											{option.text}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</>
					)}

					<Toggle
						disabled={disabled || !canModifyAccount || data.settingDisabled}
						id={id}
						isOn={!!booleanValue}
						onToggleCheckChanged={data?.onToggle}
						uncheckedColor='#ccc'
					/>
				</>
			)}
		</AccountDetailsInfoField>
	);

	// The spring animates to a fixed height, which would both clip the stacked help text and
	// collapse a disabled dependent out of view. The detail variant wants neither.
	if (isDetail) {
		return <div className={css(styleSheet.detailRow)}>{field}</div>;
	}

	return (
		<animated.div className={css(styleSheet.expandable)} style={spring}>
			{field}
		</animated.div>
	);
}
